/**
 * Clubbed tax income ("itr" source), one item per tax document type.
 *
 * Documents come from tenantExtraData.forensic_tax_items (built in the
 * backend from the consolidated Tax Returns key factor):
 *   Model 100 (annual)   — landlord ticks any of its figures; sum ÷ 12
 *   Model 130 (quarter)  — box 03 (year-to-date); ÷ months elapsed
 *                          (Q1 ÷ 3, Q2 ÷ 6, Q3 ÷ 9, Q4 ÷ 12)
 *   Model 303 (VAT)      — boxes 01+04+07+59+60; ÷ 3
 * The landlord picks which documents (and Model 100 figures) count; stored in
 * sharedProfile.income_selection:
 *   income_selection[tenantId].tenant_components.tax = ["<docId>:<field>"]
 *   (Model 130 / 303 use the field "value")
 * Monthly total = sum of the selected documents' monthly values. The "itr"
 * source keeps an annual amount (monthly × 12) because getCustomIncomeDetails
 * divides it by 12, as before.
 *
 * Default = previous hierarchy: the latest year wins; within the same year
 * Model 100 beats Model 130. Model 303 is never selected by default.
 *
 * #NOTE: the Modelo 130 IRPF calculation (selfEmployedIncomeCalculation.js)
 * is kept but not run — see getModelo130Documents below.
 *
 * Kept free of imports from ./common to avoid a circular dependency.
 */

export const TAX_SOURCE = "itr";
export const TAX_VALUE_FIELD = "value";

// Labels are translated in the components: model names via
// _t("model100" | "model130" | "model303"), figures via _t(<field key>).

const MODEL_100_BOX_FIELDS = [
  "incomeFromEmployment0018",
  "totalGrossIncome0012",
  "netIncomeReduced0025",
  "withholdings0596",
  "combinedIncome",
  "rentalIncomeGross0102",
  "interest0027",
  "dividends0029",
  "treasuryBills0030",
  "financialAssetTransfer0031",
  "insuranceIncome0032",
  "movableCapital0033",
  "subordinatedDebt0034",
  "longTermSavingsPlans0035",
];

const MODEL_100_GETTERS = {
  totalGrossIncome0012: (tr) => tr?.totalGrossIncome0012,
  incomeFromEmployment0018: (tr) => tr?.incomeFromEmployment0018,
  netIncomeReduced0025: (tr) => tr?.neverUse?.netIncomeReduced0025,
  withholdings0596: (tr) => tr?.neverUse?.withholdings0596,
  combinedIncome: (tr) => tr?.combinedIncome,
  rentalIncomeGross0102: (tr) => tr?.rentalIncomeGross0102,
  interest0027: (tr) => tr?.savings?.interest0027,
  dividends0029: (tr) => tr?.savings?.dividends0029,
  treasuryBills0030: (tr) => tr?.savings?.treasuryBills0030,
  financialAssetTransfer0031: (tr) => tr?.savings?.financialAssetTransfer0031,
  insuranceIncome0032: (tr) => tr?.savings?.insuranceIncome0032,
  movableCapital0033: (tr) => tr?.savings?.movableCapital0033,
  subordinatedDebt0034: (tr) => tr?.savings?.subordinatedDebt0034,
  longTermSavingsPlans0035: (tr) => tr?.savings?.longTermSavingsPlans0035,
  grossIncome: (tr) => tr?.grossIncome,
  netIncomeAfterDeductions: (tr) => tr?.netIncomeAfterDeductions,
};

const MODEL_303_SALES = [
  "salesSuperReducedRate01",
  "salesReducedRate04",
  "salesStandardRate07",
];
const MODEL_303_INTRA_EU = ["intraEuDeliveries59", "exports60"];

// same as getUpdatedExchangeIncome in ./common
const toEur = (amount, currency, originalUser) => {
  const rate = originalUser?.common?.currency_exchange?.rates?.[currency] ?? 1;
  return Math.round(amount * rate);
};

const toNumberOrNull = (v) =>
  v === null || v === undefined || v === "" || !Number.isFinite(Number(v))
    ? null
    : Number(v);

const parseQuarter = (value) => {
  const match = String(value ?? "").match(/\bQ([1-4])\b|\b([1-4])T\b/i);
  return match ? Number(match[1] ?? match[2]) : null;
};

const getModel130Quarter = (tr) => {
  const remarks =
    typeof tr?.remarks === "object"
      ? `${tr?.remarks?.en ?? ""} ${tr?.remarks?.es ?? ""}`
      : tr?.remarks;
  return parseQuarter(tr?.vatInvoicing?.quarter) ?? parseQuarter(remarks);
};

const isModel100Return = (tr) =>
  tr?.reportType === "annual" ||
  (!tr?.reportType &&
    MODEL_100_BOX_FIELDS.some(
      (key) => toNumberOrNull(MODEL_100_GETTERS[key](tr)) !== null
    ));

/**
 * Same as buildTaxItems in the backend (uno-backend utils/common.js) — used
 * when the documents are not stored yet but forensic key factors are.
 */
export const buildTaxDocumentsFromForensic = (forensicAnalysis) => {
  const taxKF =
    forensicAnalysis?.["Tax Returns"] ?? forensicAnalysis?.["Tax returns"];
  const taxReturns = taxKF?.dataInJSON?.taxReturns ?? [];
  const verified = taxKF?.verifiedSource ?? false;
  const seen = {};
  const items = [];
  const push = (item) => {
    seen[item.id] = (seen[item.id] ?? 0) + 1;
    items.push({
      ...item,
      id: seen[item.id] > 1 ? `${item.id}#${seen[item.id]}` : item.id,
    });
  };

  (Array.isArray(taxReturns) ? taxReturns : [])
    .filter((tr) => tr && typeof tr === "object")
    .forEach((tr) => {
      const year = Number(tr?.taxReturnYear);
      if (!Number.isFinite(year)) return;
      const base = {
        year,
        origin: tr?.taxReportOrigin ?? null,
        currency: tr?.currencyCode ?? null,
        verified,
      };
      if (isModel100Return(tr)) {
        const fields = {};
        Object.entries(MODEL_100_GETTERS).forEach(([key, get]) => {
          fields[key] = toNumberOrNull(get(tr));
        });
        push({
          ...base,
          id: `100|${year}`,
          model: "100",
          quarter: null,
          fields,
        });
      }
      if (toNumberOrNull(tr?.netAccumulatedIncome03) !== null) {
        const quarter = getModel130Quarter(tr);
        push({
          ...base,
          id: `130|${year}${quarter ? `|Q${quarter}` : ""}`,
          model: "130",
          quarter,
          fields: {
            netAccumulatedIncome03: toNumberOrNull(tr.netAccumulatedIncome03),
          },
        });
      }
      const vat = tr?.vatInvoicing;
      const boxes = [...MODEL_303_SALES, ...MODEL_303_INTRA_EU];
      if (
        vat &&
        typeof vat === "object" &&
        boxes.some((key) => toNumberOrNull(vat?.[key]) !== null)
      ) {
        const quarter = parseQuarter(vat?.quarter);
        const fields = {};
        boxes.forEach((key) => {
          fields[key] = toNumberOrNull(vat?.[key]);
        });
        push({
          ...base,
          id: `303|${year}${quarter ? `|Q${quarter}` : ""}`,
          model: "303",
          quarter,
          quarters_included: Array.isArray(vat?.quartersIncluded)
            ? vat.quartersIncluded
            : [],
          fields,
        });
      }
    });
  return items;
};

/**
 * Tax documents for a user. Stored documents (new shape, with `model`) are
 * preferred; else they are built from the forensic key factors; else null
 * (legacy forensic_financial_data.itr is used unchanged).
 */
export const getTaxDocuments = (user, forensicAnalysis = null) => {
  const stored = user?.tenant_extra_field_id?.forensic_tax_items;
  // empty = not extracted yet (Mongoose returns [] for unset array fields)
  if (
    Array.isArray(stored) &&
    stored.length > 0 &&
    stored.every((d) => d?.model)
  )
    return stored.filter(hasAnyTaxValue);
  if (forensicAnalysis)
    return buildTaxDocumentsFromForensic(forensicAnalysis).filter(
      hasAnyTaxValue
    );
  return null;
};

// "Model 130 Q3 2026", "Model 100 2025" (_t optional, for the "model" word)
// foreign return: origin set and not "local"
export const isForeignTaxDoc = (doc) =>
  Boolean(doc?.origin) && String(doc.origin).toLowerCase() !== "local";

// translation key of a foreign document's name (no Spanish model number)
const FOREIGN_TAX_DOC_KEYS = {
  100: "annualTaxReport",
  130: "quarterlyTaxPayment",
  303: "quarterlyVatReport",
};

// group heading: "Model 100" / foreign "Annual tax report"
export const getTaxGroupLabel = (model, foreign, _t) =>
  foreign && FOREIGN_TAX_DOC_KEYS[model]
    ? _t(FOREIGN_TAX_DOC_KEYS[model])
    : _t(`model${model}`);

// "Model 130 Q3 2026", "Model 100 2025"; foreign "Annual tax report 2025"
export const getTaxDocTitle = (doc, _t) => {
  const t = _t ?? ((k) => k);
  const name =
    isForeignTaxDoc(doc) && FOREIGN_TAX_DOC_KEYS[doc?.model]
      ? t(FOREIGN_TAX_DOC_KEYS[doc.model])
      : `${_t ? _t("model") : "Model"} ${doc?.model}`;
  return `${name} ${doc?.quarter ? `Q${doc.quarter} ` : ""}${
    doc?.year ?? ""
  }`.trim();
};

// Model 100 figures shown for a document (others use TAX_VALUE_FIELD)
export const getTaxDocFieldKeys = (doc, isDeaTaxRule = false) => {
  if (doc?.model !== "100") return [TAX_VALUE_FIELD];
  const keys = [...MODEL_100_BOX_FIELDS];
  if (doc?.origin?.toLowerCase() !== "local") keys.unshift("grossIncome");
  if (isDeaTaxRule) keys.unshift("netIncomeAfterDeductions");
  // figures that are missing or 0 are not shown
  return keys.filter((k) => hasTaxValue(doc?.fields?.[k]));
};

const hasTaxValue = (v) => {
  const n = toNumberOrNull(v);
  return n !== null && n !== 0;
};

// a document is shown only when it has at least one figure with a value
const hasAnyTaxValue = (doc) =>
  doc?.model === "100"
    ? Object.values(doc?.fields ?? {}).some(hasTaxValue)
    : hasTaxValue(getTaxDocFieldValue(doc));

// months the figure covers: 100 = 12, 130 = months elapsed, 303 = 3
export const getTaxDocMonths = (doc) => {
  if (doc?.model === "130") return doc?.quarter ? doc.quarter * 3 : 12;
  if (doc?.model === "303") return 3;
  return 12;
};

const sumKeys = (doc, keys) =>
  keys.reduce((acc, k) => acc + (toNumberOrNull(doc?.fields?.[k]) ?? 0), 0);

// value of one figure in the document's currency (before ÷ months)
export const getTaxDocFieldValue = (doc, field) => {
  if (doc?.model === "130") return sumKeys(doc, ["netAccumulatedIncome03"]);
  if (doc?.model === "303") {
    if (field === "sales") return sumKeys(doc, MODEL_303_SALES);
    if (field === "intraEu") return sumKeys(doc, MODEL_303_INTRA_EU);
    return sumKeys(doc, [...MODEL_303_SALES, ...MODEL_303_INTRA_EU]);
  }
  return toNumberOrNull(doc?.fields?.[field]) ?? 0;
};

export const getTaxFieldId = (doc, field) => `${doc?.id}:${field}`;

const splitTaxId = (id) => {
  const idx = String(id).lastIndexOf(":");
  return idx === -1
    ? [String(id), ""]
    : [String(id).slice(0, idx), String(id).slice(idx + 1)];
};

// the field the previous logic used for a Model 100
const getLegacyModel100Field = (doc, isDeaTaxRule) => {
  if (isDeaTaxRule) return "netIncomeAfterDeductions";
  if (doc?.origin?.toLowerCase() !== "local") return "grossIncome";
  return toNumberOrNull(doc?.fields?.combinedIncome) !== null
    ? "combinedIncome"
    : "grossIncome";
};

/**
 * Document selected by default (previous hierarchy): the latest year wins;
 * within the same year Model 100 beats Model 130. Only documents with an
 * amount qualify. Model 303 is never the default.
 */
export const pickDefaultTaxDoc = (docs = [], isDeaTaxRule = false) => {
  const candidates = (docs ?? []).filter((d) => {
    if (d?.model === "100")
      return (
        getTaxDocFieldValue(d, getLegacyModel100Field(d, isDeaTaxRule)) > 0
      );
    if (d?.model === "130") return getTaxDocFieldValue(d) > 0;
    return false;
  });
  return (
    candidates.sort(
      (a, b) =>
        (b.year ?? 0) - (a.year ?? 0) ||
        (a.model === "100" ? -1 : 0) - (b.model === "100" ? -1 : 0) ||
        (b.quarter ?? 0) - (a.quarter ?? 0)
    )[0] ?? null
  );
};

export const getDefaultTaxIds = (docs = [], isDeaTaxRule = false) => {
  const doc = pickDefaultTaxDoc(docs, isDeaTaxRule);
  if (!doc) return [];
  return [
    getTaxFieldId(
      doc,
      doc.model === "100"
        ? getLegacyModel100Field(doc, isDeaTaxRule)
        : TAX_VALUE_FIELD
    ),
  ];
};

/**
 * Saved ids that still exist, else the default. Ids saved in an older
 * format (none match the current documents) are treated as not saved.
 */
export const resolveTaxIds = (
  docs = [],
  savedIds = null,
  isDeaTaxRule = false
) => {
  if (!docs?.length) return [];
  if (Array.isArray(savedIds)) {
    const valid = new Set(
      docs.flatMap((d) =>
        getTaxDocFieldKeys(d, isDeaTaxRule).map((f) => getTaxFieldId(d, f))
      )
    );
    const kept = savedIds.filter((id) => valid.has(id));
    if (kept.length || savedIds.length === 0) return kept;
  }
  return getDefaultTaxIds(docs, isDeaTaxRule);
};

// { [docId]: [fields] } for the selected ids
export const getTaxSelectionMap = (ids = []) => {
  const map = {};
  ids.forEach((id) => {
    const [docId, field] = splitTaxId(id);
    if (!map[docId]) map[docId] = [];
    if (field && !map[docId].includes(field)) map[docId].push(field);
  });
  return map;
};

// monthly EUR amount of one document for the given fields
export const getTaxDocMonthlyAmount = (doc, fields = [], originalUser) => {
  if (!doc || !fields.length) return 0;
  const total =
    doc.model === "100"
      ? fields.reduce((acc, f) => acc + getTaxDocFieldValue(doc, f), 0)
      : getTaxDocFieldValue(doc);
  return toEur(total / getTaxDocMonths(doc), doc.currency, originalUser) || 0;
};

// monthly EUR total of all selected documents
export const getTaxMonthlyAmount = (docs = [], ids = [], originalUser) => {
  const map = getTaxSelectionMap(ids);
  return (docs ?? []).reduce(
    (acc, d) => acc + getTaxDocMonthlyAmount(d, map[d.id] ?? [], originalUser),
    0
  );
};

/**
 * #NOTE: kept for the Modelo 130 calculation (calculateModelo130Income),
 * currently NOT run. Builds its input from the latest year's quarterly
 * returns exactly as DetailsIncomeInfo did before (baseline cb3319f50).
 */
export const getModelo130Documents = (taxReturns = []) => {
  const latestYear = taxReturns.reduce((maxYear, tr) => {
    const year = Number(tr?.taxReturnYear);
    return Number.isFinite(year) ? Math.max(maxYear, year) : maxYear;
  }, 0);
  return taxReturns
    .filter(
      (tr) => Number(tr?.taxReturnYear) === latestYear && tr?.quarterNumber
    )
    .map((tr) => {
      const hasIncomeValues =
        tr?.combinedIncome != null && tr?.grossIncome != null;
      const calculatedNet = hasIncomeValues
        ? Number(tr.grossIncome) - Number(tr.combinedIncome)
        : null;
      return {
        year: Number(tr?.taxReturnYear),
        quarter: Number(tr?.quarterNumber),
        annualizedNet:
          tr?.netIncome != null
            ? Number(tr.netIncome)
            : tr?.taxBaseIncome != null
              ? Number(tr.taxBaseIncome)
              : calculatedNet,
      };
    })
    .filter(
      (tr) =>
        Number.isFinite(tr.year) &&
        Number.isFinite(tr.quarter) &&
        tr.quarter >= 1 &&
        tr.quarter <= 4 &&
        tr.annualizedNet != null &&
        Number.isFinite(tr.annualizedNet)
    );
};
